// QR Kiosk v2 — §8.2 welcome screen. Replaces the 3 canned questions with a
// headline + a service-reports entry-point TILE (the future §9 diagnostic-copilot
// entries — styled as destinations, not question pills). Tapping a tile sends a well-formed
// prompt today (label ≠ prompt so the label stays short). Phone top-anchors so it
// survives the keyboard-up region (§8.1); tablet+ stays vertically centered.
// Tokens + glyphs only.
import React from "react";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import { Glyph, GD, Spark } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";

function EntryTile({ glyph, label, onClick }: { glyph: string; label: string; onClick: () => void }) {
  const press = usePressed();
  return (
    <button
      type="button"
      {...press.pressHandlers}
      onClick={onClick}
      style={{
        width: "100%",
        maxWidth: 340,
        minHeight: 56,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 14,
        border: "1px solid var(--midas-line)",
        background: "var(--card)",
        boxShadow: "var(--shadow)",
        cursor: "pointer",
        ...press.pressStyle,
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          background: "var(--midas-soft)",
          color: "var(--midas)",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        <Glyph d={glyph} size={20} />
      </span>
      <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", textAlign: "left", flex: 1, minWidth: 0 }}>
        {label}
      </span>
      <Glyph d={GD.chev} size={16} style={{ color: "var(--muted)" }} />
    </button>
  );
}

export function MidasWelcome({ onSendPrompt }: { onSendPrompt: (text: string) => void }) {
  const { translate } = useTranslation("public");
  const { isPhone } = useBreakpoint();
  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: isPhone ? "flex-start" : "center",
        gap: 18,
        padding: isPhone ? "18px 8px 24px" : "24px 8px",
      }}
    >
      <span style={{ color: "var(--midas)", display: "inline-flex" }}>
        <Spark size={28} />
      </span>
      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", textAlign: "center", lineHeight: 1.35, maxWidth: 320 }}>
        {translate("kioskV2MidasWelcomeTitle")}
      </div>
      {/* Single entry tile. The "My tasks" tile was REMOVED (owner, demo day 2026-08-04):
          a kiosk visitor is a temporary PIN-based user with no assigned tasks, so the
          tile could only ever resolve to an empty answer. The column keeps its gap for
          when a second §9 diagnostic entry point lands; with one child no gap renders. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", maxWidth: 340, alignItems: "center" }}>
        <EntryTile
          glyph={GD.clip}
          label={translate("kioskV2MidasChipReports")}
          onClick={() => onSendPrompt(translate("kioskV2MidasChipReportsPrompt"))}
        />
      </div>
    </div>
  );
}

export default MidasWelcome;
